import {
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { BulkValidateUsersResponse } from '@/lib/types/mise/user';
import type { ComparisonType } from './BulkCreateUserUploadStep';

interface BulkCreateUserReviewStepProps {
  validateResult: BulkValidateUsersResponse;
  /** CSVの user_name 列(空欄はメールアドレスから生成)を行番号で引けるようにしたもの */
  userNamesByRowIndex: Record<number, string>;
  defaultAccountLabel: string;
  comparisonType: ComparisonType;
  comparisonSellerLabel: string;
  comparisonSellerGroupLabel: string;
  suppressInvitationEmail: boolean;
  createError: string | null;
}

export function BulkCreateUserReviewStep({
  validateResult,
  userNamesByRowIndex,
  defaultAccountLabel,
  comparisonType,
  comparisonSellerLabel,
  comparisonSellerGroupLabel,
  suppressInvitationEmail,
  createError,
}: BulkCreateUserReviewStepProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <Typography>
        {validateResult.summary.total}件中、{validateResult.summary.valid}件登録可能、
        {validateResult.summary.invalid}件エラー
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2">
          <strong>デフォルトアカウント:</strong> {defaultAccountLabel}
        </Typography>
        {comparisonType === 'seller_group_mapping' && (
          <Typography variant="body2">
            <strong>比較店舗グループ:</strong> 各店舗の店舗グループマッピングに従う
          </Typography>
        )}
        {comparisonType === 'seller' && (
          <Typography variant="body2">
            <strong>比較店舗:</strong> {comparisonSellerLabel}
          </Typography>
        )}
        {comparisonType === 'seller_group' && (
          <Typography variant="body2">
            <strong>比較店舗グループ:</strong> {comparisonSellerGroupLabel}
          </Typography>
        )}
        <Typography variant="body2">
          <strong>招待メール送信抑制:</strong> {suppressInvitationEmail ? 'はい' : 'いいえ'}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="CSV取り込み結果テーブル">
          <TableHead>
            <TableRow>
              <TableCell>行番号</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>ユーザー名</TableCell>
              <TableCell>店舗ID</TableCell>
              <TableCell>店舗名</TableCell>
              {comparisonType === 'seller_group_mapping' && <TableCell>比較店舗グループ</TableCell>}
              <TableCell>ステータス</TableCell>
              <TableCell>エラー</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validateResult.results.map((row) => (
              <TableRow key={row.row_index}>
                <TableCell>{row.row_index + 1}</TableCell>
                <TableCell>{row.user_email}</TableCell>
                <TableCell>{userNamesByRowIndex[row.row_index]}</TableCell>
                <TableCell>{row.seller_id}</TableCell>
                <TableCell>{row.seller_name ?? '—'}</TableCell>
                {comparisonType === 'seller_group_mapping' && (
                  <TableCell>
                    {/* 同名の店舗グループ（例: ブランドごとの「関東」）を見分けられるよう ID も出す */}
                    {row.comparison_seller_group_id
                      ? `${row.comparison_seller_group_name ?? '—'}（${row.comparison_seller_group_id}）`
                      : '—'}
                  </TableCell>
                )}
                <TableCell>
                  {row.status === 'ok' ? (
                    <Chip label="OK" color="success" size="small" />
                  ) : (
                    <Chip label="エラー" color="error" size="small" />
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.errors.join('\n')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {createError && <Alert severity="error">{createError}</Alert>}
    </Box>
  );
}
